import React, { useEffect } from "react";
import { useAppSelector } from "../../../hooks/hooks";
import {
  selectVehicleReport,
  selectVehicleReportStatus,
} from "../../../features/reports/reportsSlice";
import {
  CollapsableItemList,
  Container,
  ContainerLineItem,
  Icon,
  IconList,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";

import {
  VehicleReportType,
  Standard,
} from "../../../features/reports/reportTypes";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";

const StandardFeatures: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const [standards, setStandards] = React.useState<Standard[]>([]);

  let pulledReport = useAppSelector(selectVehicleReport) as VehicleReportType;
  const vehicleReportStatus = useAppSelector(selectVehicleReportStatus);

  const loading = vehicleReportStatus === "loading";

  useEffect(() => {
    if (
      pulledReport &&
      pulledReport.features &&
      pulledReport.features.standards
    ) {
      setStandards(pulledReport.features.standards || []);
    }
  }, [pulledReport]);

  const groupedByCategory = standards.reduce<Record<string, Standard[]>>(
    (acc, standard) => {
      if (!acc[standard.category]) {
        acc[standard.category] = [];
      }
      acc[standard.category].push(standard);
      return acc;
    },
    {},
  );

  return (
    <Padding
      space="none"
      breakpointStyles={{
        mobile: {
          left: "md",
          right: "md",
        },
      }}
    >
      <Container
        containerId={containerId}
        containerTitle={`Standard Features ${
          loading
            ? ""
            : `(${standards && standards.length ? standards.length : 0})`
        }`}
      >
        {loading ||
        (pulledReport &&
          pulledReport.features &&
          pulledReport.features.standards &&
          pulledReport.features.standards.length > 0) ? (
          <Container altColor="grey">
            <Padding space="sm">
              <Layout
                type="flex"
                flexProps={{ gap: "xs", direction: "column" }}
              >
                <Layout
                  type="flex"
                  flexProps={{ gap: "xxs", alignItems: "center" }}
                >
                  <Text tag="div" size="xs">
                    These features provide the highest resale value. This is not
                    a comprehensive list of all features on this vehicle.
                  </Text>
                </Layout>
              </Layout>
            </Padding>
          </Container>
        ) : null}
        {loading ? (
          <LoadingContainerLineItem
            lineItemHeader
            lineItemSubHeader
            numberToLoad={3}
          />
        ) : pulledReport &&
          pulledReport.features &&
          pulledReport.features.standards &&
          pulledReport.features.standards.length > 0 ? (
          <>
            <CollapsableItemList
              numberToShow={5}
              isOpen={printReady ? true : false}
            >
              {Object.entries(groupedByCategory).map(
                ([category, standardsArr], index) => (
                  <ContainerLineItem
                    key={category}
                    isOpen={index === 0 || printReady ? true : false}
                    lineItemHeader={
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "center",
                          direction: "row",
                        }}
                      >
                        <Text tag="div">{category}</Text>
                      </Layout>
                    }
                    children={
                      <Container>
                        <Padding space="md" top="none">
                          <Container altColor="grey" bodyBorderRadius={true}>
                            <Padding space="md">
                              <Layout
                                type="grid"
                                gridProps={{
                                  gap: "xs",
                                  templateColumns: "2-column",
                                }}
                              >
                                {standardsArr.map((standardItem, index) => (
                                  <Layout
                                    type="flex"
                                    flexProps={{
                                      gap: "xs",
                                      direction: "row",
                                    }}
                                    key={`standard-${index}`}
                                  >
                                    <Text tag="div" color="success">
                                      <Icon
                                        name={IconList.IconCircleCheck}
                                        size={18}
                                      />
                                    </Text>
                                    <Text tag="div" size="sm">
                                      {standardItem.description}
                                    </Text>
                                  </Layout>
                                ))}
                              </Layout>
                            </Padding>
                          </Container>
                        </Padding>
                      </Container>
                    }
                  />
                ),
              )}
            </CollapsableItemList>
          </>
        ) : (
          <Padding space="md">
            <Text size="sm" fontWeight="bold">
              No standard high-value features were found on this vehicle.
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default StandardFeatures;
